// Onboarding Module Types
export interface PetProfile {
  id: string;
  userId: string;
  name: string;
  type: 'dog' | 'cat' | 'other';
  breed?: string;
  age?: number;
  birthday?: Timestamp | Date | string; // Calculated from age estimate, user can edit later
  gender?: 'male' | 'female' | 'unknown';

  // Onboarding and AI Analysis
  onboardingCompleted?: boolean;
  onboardingStep?: number;
  onboardingStartedAt?: Timestamp;
  aiAnalysisData?: {
    breedConfidence?: number;
    ageConfidence?: number;
    sexConfidence?: number;
    analysisDate?: Date | Timestamp; // Allow both Date and Timestamp
    analysisImageUrl?: string;
    description?: string; // Enthusiastic AI description of pet's appearance
  };

  // Health information
  healthConditions?: string;
  medications?: string;
  vaccinations?: {
    type: string;
    date: Timestamp;
    nextDue?: Timestamp;
  }[];
  weight?: {
    value: number;
    unit: 'lbs' | 'kg';
  };

  // Care preferences
  activityLevel?: 'low' | 'moderate' | 'high';
  dietType?: string;
  specialNeeds?: string;

  // Behavioral information
  temperament?: string;
  trainingLevel?: 'beginner' | 'intermediate' | 'advanced';
  behavioralIssues?: string;

  // Intake Form Specifics
  emergencyContact?: string;
  vetContact?: string;
  foodAllergies?: { hasAllergies: boolean; details?: string; };
  freshTreatPermission?: boolean;
  medicalStatus?: { isSpayedNeutered: boolean; };
  pottyHabits?: string;
  barkingHabits?: string;
  boardingDates?: { from: string; to: string };
  chewingHabits?: { hasHabit: boolean; details?: string };
  separationAnxiety?: string;
  favoriteThings?: string[];
  walkingPreferences?: string;
  favoriteThingsOther?: string;
  dailyRoutine?: string;
  exerciseNeeds?: string;
  sleepingArrangement?: string;
  additionalNotes?: string;
  isIntakePending?: boolean;

  // Metadata
  createdAt: Timestamp;
  updatedAt: Timestamp;
  profilePhoto?: string;
  isActive: boolean;
}

export interface OnboardingState {
  currentStep: OnboardingStep;
  petData: Partial<PetProfile>;
  userLanguage: 'en' | 'zh-CN' | 'es' | 'fr';
  isLoading: boolean;
  error?: string;
}

export enum OnboardingStep {
  WELCOME = 1,
  PHOTO_UPLOAD = 2,
  CONFIRM_DETAILS = 3,
  COMPLETED = 4
}

export interface ImageAnalysisResult {
  breed: string;
  confidence: number;
  age: number;
  ageConfidence: number;
  sex: 'male' | 'female' | 'unknown';
  sexConfidence: number;
  description: string;
  analysisImageUrl: string;
}

export interface OnboardingConfig {
  enableAI: boolean;
  maxRetries: number;
  timeout: number;
  supportedLanguages: ('en' | 'zh-CN' | 'es' | 'fr')[];
}

export interface OnboardingCallbacks {
  onComplete?: (petProfile: PetProfile) => void;
  onCancel?: () => void;
  onError?: (error: string) => void;
  onStepChange?: (step: OnboardingStep) => void;
}

// Language-specific text interfaces
export interface OnboardingTexts {
  welcome: {
    title: string;
    subtitle: string;
    description: string;
    startButton: string;
  };
  photoUpload: {
    title: string;
    description: string;
    uploadButton: string;
    skipButton: string;
    analyzing: string;
  };
  aiAnalysis: {
    title: string;
    analyzing: string;
    resultsTitle: string;
    breedLabel: string;
    ageLabel: string;
    sexLabel: string;
    confidenceLabel: string;
    editButton: string;
    confirmButton: string;
  };
  confirmDetails: {
    title: string;
    nameLabel: string;
    breedLabel: string;
    ageLabel: string;
    sexLabel: string;
    saveButton: string;
    backButton: string;
  };
  completed: {
    title: string;
    message: string;
    continueButton: string;
    addAnotherPetButton: string;
  };
  errors: {
    uploadFailed: string;
    analysisFailed: string;
    saveFailed: string;
    genericError: string;
  };
}

// Utility types
export type Timestamp = {
  seconds: number;
  nanoseconds: number;
};

export type OnboardingProgress = {
  step: OnboardingStep;
  completed: boolean;
  timestamp: Timestamp;
};
